import React, { useState, useRef, useEffect } from 'react';
import { 
  DownloadIcon,
  ColorSwatchIcon,
  DuplicateIcon,
  CheckIcon,
  PencilAltIcon,
  PhotographIcon,
  BadgeCheckIcon,
  CogIcon
} from '@heroicons/react/outline';
import { toPng } from 'html-to-image';

const QuoteCard = () => {
  const [quote, setQuote] = useState('');
  const [author, setAuthor] = useState('');
  const [episode, setEpisode] = useState('');
  const [podcastName, setPodcastName] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('gradient-blue');
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);

  // Text customization states
  const [fontFamily, setFontFamily] = useState('ui-sans-serif, system-ui, sans-serif');
  const [fontSize, setFontSize] = useState(36);
  const [customTextColor, setCustomTextColor] = useState('');
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [letterSpacing, setLetterSpacing] = useState(0);
  const [lineHeight, setLineHeight] = useState(1.2);
  const [useCustomTextColor, setUseCustomTextColor] = useState(false);

  // Background customization states
  const [backgroundType, setBackgroundType] = useState('theme'); // 'theme', 'solid', 'image', 'gradient'
  const [customBackgroundColor, setCustomBackgroundColor] = useState('#4055A8');
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [backgroundOpacity, setBackgroundOpacity] = useState(0);
  const [backgroundBlur, setBackgroundBlur] = useState(0);

  // Gradient customization states
  const [gradientColor1, setGradientColor1] = useState('#4055A8');
  const [gradientColor2, setGradientColor2] = useState('#2D3A8C');
  const [gradientDirection, setGradientDirection] = useState('to bottom right'); // CSS gradient direction

  // Branding/Logo states
  const [showLogo, setShowLogo] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [logoPosition, setLogoPosition] = useState('bottom-right'); // 'top-left', 'top-right', 'bottom-left', 'bottom-right'
  const [logoSize, setLogoSize] = useState(40); // Size in pixels
  const [logoOpacity, setLogoOpacity] = useState(0.8); // For watermark effect

  // Tab and collapsible sections state for panel organization
  const [activeTab, setActiveTab] = useState('content');
  const [expandedSections, setExpandedSections] = useState({
    textStyle: true,
    textAdvanced: false,
    backgroundType: true,
    backgroundCustom: false,
    logoSettings: false
  });

  // automatically open customization when non-theme background selected
  useEffect(() => {
    if (backgroundType !== 'theme') {
      setExpandedSections(prev => ({ ...prev, backgroundCustom: true }));
    }
  }, [backgroundType]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Add custom slider styles and load Google Fonts
  useEffect(() => {
    // Add custom slider styles
    const style = document.createElement('style');
    style.textContent = `
      .slider {
        -webkit-appearance: none;
        appearance: none;
        background: transparent;
        cursor: pointer;
      }
      
      .slider::-webkit-slider-track {
        background: #e5e7eb;
        height: 8px;
        border-radius: 4px;
      }
      
      .slider::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        height: 20px;
        width: 20px;
        border-radius: 50%;
        background: #4f46e5;
        cursor: pointer;
        border: 2px solid #ffffff;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }
      
      .slider::-moz-range-track {
        background: #e5e7eb;
        height: 8px;
        border-radius: 4px;
        border: none;
      }
      
      .slider::-moz-range-thumb {
        height: 20px;
        width: 20px;
        border-radius: 50%;
        background: #4f46e5;
        cursor: pointer;
        border: 2px solid #ffffff;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }
    `;
    document.head.appendChild(style);

    // Load Google Fonts for QuoteCard
    const fontLink = document.createElement('link');
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Roboto:ital,wght@0,400;0,700;1,400&family=Open+Sans:ital,wght@0,400;0,700;1,400&family=Montserrat:ital,wght@0,400;0,700;1,400&family=Lora:ital,wght@0,400;0,700;1,400&family=Merriweather:ital,wght@0,400;0,700;1,400&family=Poppins:ital,wght@0,400;0,700;1,400&family=Source+Sans+Pro:ital,wght@0,400;0,700;1,400&family=Raleway:ital,wght@0,400;0,700;1,400&family=Nunito:ital,wght@0,400;0,700;1,400&family=Oswald:wght@400;700&family=Dancing+Script:wght@400;700&display=swap';
    fontLink.rel = 'stylesheet';
    fontLink.id = 'quote-card-fonts';
    
    // Only add if not already present
    if (!document.getElementById('quote-card-fonts')) {
      document.head.appendChild(fontLink);
    }
    
    return () => {
      // Clean up when component unmounts
      const existingStyle = document.head.querySelector('style');
      if (existingStyle && existingStyle.textContent.includes('.slider')) {
        document.head.removeChild(existingStyle);
      }
      const existingLink = document.getElementById('quote-card-fonts');
      if (existingLink) {
        document.head.removeChild(existingLink);
      }
    };
  }, []);

  const themes = {
    'gradient-blue': {
      background: 'bg-gradient-to-br from-primary-600 to-primary-800',
      text: 'text-white',
      accent: 'text-primary-200'
    },
    'gradient-orange': {
      background: 'bg-gradient-to-br from-secondary-500 to-secondary-700',
      text: 'text-white',
      accent: 'text-secondary-200'
    },
    'minimal-light': {
      background: 'bg-white border-2 border-neutral-200',
      text: 'text-neutral-900',
      accent: 'text-neutral-600'
    },
    'minimal-dark': {
      background: 'bg-neutral-900',
      text: 'text-white',
      accent: 'text-neutral-400'
    },
    'brand-gradient': {
      background: 'bg-gradient-to-r from-primary-600 via-secondary-500 to-primary-800',
      text: 'text-white',
      accent: 'text-neutral-100'
    }
  };

  const fontCategories = {
    'Sans-Serif': {
      description: 'Clean, modern, and straightforward. High readability for digital platforms.',
      fonts: [
        { value: 'ui-sans-serif, system-ui, sans-serif', label: 'System Sans-Serif', class: 'font-sans' },
        { value: 'Inter', label: 'Inter', class: 'font-sans' },
        { value: 'Lexend', label: 'Lexend', class: 'font-display' },
        { value: '"Open Sans", sans-serif', label: 'Open Sans', class: 'font-sans' },
        { value: '"Roboto", sans-serif', label: 'Roboto', class: 'font-sans' },
        { value: '"Montserrat", sans-serif', label: 'Montserrat', class: 'font-sans' },
        { value: '"Poppins", sans-serif', label: 'Poppins', class: 'font-sans' },
        { value: '"Source Sans Pro", sans-serif', label: 'Source Sans Pro', class: 'font-sans' },
        { value: '"Raleway", sans-serif', label: 'Raleway', class: 'font-sans' },
        { value: '"Nunito", sans-serif', label: 'Nunito', class: 'font-sans' },
        { value: 'Arial', label: 'Arial', class: 'font-sans' },
        { value: 'Helvetica', label: 'Helvetica', class: 'font-sans' }
      ]
    },
    'Serif': {
      description: 'Classic, elegant, and sophisticated. Adds authority and tradition.',
      fonts: [
        { value: 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif', label: 'System Serif', class: 'font-serif' },
        { value: '"Playfair Display", serif', label: 'Playfair Display', class: 'font-serif' },
        { value: '"Lora", serif', label: 'Lora', class: 'font-serif' },
        { value: '"Merriweather", serif', label: 'Merriweather', class: 'font-serif' },
        { value: 'Georgia', label: 'Georgia', class: 'font-serif' },
        { value: 'Times New Roman', label: 'Times New Roman', class: 'font-serif' },
        { value: 'Cambria', label: 'Cambria', class: 'font-serif' },
        { value: 'Times', label: 'Times', class: 'font-serif' }
      ]
    },
    'Display & Impact': {
      description: 'Bold, attention-grabbing fonts. Perfect for powerful statements and headlines.',
      fonts: [
        { value: '"Oswald", sans-serif', label: 'Oswald', class: 'font-sans' },
        { value: '"Montserrat", sans-serif', label: 'Montserrat Bold', class: 'font-sans' },
        { value: 'Impact', label: 'Impact', class: 'font-sans' },
        { value: 'Arial Black', label: 'Arial Black', class: 'font-sans' }
      ]
    },
    'Script & Creative': {
      description: 'Flowing, artistic fonts. Perfect for inspirational or personal quotes.',
      fonts: [
        { value: '"Dancing Script", cursive', label: 'Dancing Script', class: 'font-serif' },
        { value: 'cursive', label: 'Cursive (System Default)', class: 'font-serif' },
        { value: 'fantasy', label: 'Fantasy (System Default)', class: 'font-serif' }
      ]
    },
    'Monospace': {
      description: 'Fixed-width fonts. Great for tech quotes, coding content, or modern minimalist designs.',
      fonts: [
        { value: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', label: 'System Monospace', class: 'font-mono' },
        { value: 'Monaco', label: 'Monaco', class: 'font-mono' },
        { value: 'Consolas', label: 'Consolas', class: 'font-mono' },
        { value: 'Menlo', label: 'Menlo', class: 'font-mono' },
        { value: 'Courier New', label: 'Courier New', class: 'font-mono' }
      ]
    }
  };

  const currentTheme = themes[selectedTheme];

  // Generate custom text styles
  const getTextStyles = () => {
    const baseStyles = {
      fontFamily: fontFamily,
      fontSize: `${fontSize}px`,
      fontWeight: isBold ? 'bold' : 'normal',
      fontStyle: isItalic ? 'italic' : 'normal',
      letterSpacing: `${letterSpacing}px`,
      lineHeight: lineHeight,
    };

    if (useCustomTextColor && customTextColor) {
      baseStyles.color = customTextColor;
    }

    return baseStyles;
  };

  const handleDownload = async () => {
    if (cardRef.current) {
      try {
        const dataUrl = await toPng(cardRef.current, {
          quality: 1.0,
          pixelRatio: 3,
          width: 384,
          height: 384
        });
        
        const link = document.createElement('a');
        link.download = `quote-card-${Date.now()}.png`;
        link.href = dataUrl;
        link.click();
      } catch (error) {
        console.error('Error generating image:', error);
      }
    }
  };

  const handleCopyImage = async () => {
    if (cardRef.current) {
      try {
        // Check if we're on Safari (Mac/iOS)
        const isSafari = /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor);
        const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        
        if (navigator.clipboard && ClipboardItem && (isSafari || isMac || isIOS)) {
          // Safari-specific approach: Use promise in ClipboardItem
          const makeImagePromise = async () => {
            const canvas = await toPng(cardRef.current, {
              quality: 1.0,
              pixelRatio: 3,
              width: 384,
              height: 384
            });
            const response = await fetch(canvas);
            return await response.blob();
          };

          await navigator.clipboard.write([
            new ClipboardItem({
              'image/png': makeImagePromise()
            })
          ]);
          
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } else if (navigator.clipboard && ClipboardItem) {
          // Standard approach for other browsers
          const canvas = await toPng(cardRef.current, {
            quality: 1.0,
            pixelRatio: 3,
            width: 384,
            height: 384
          });
          
          const response = await fetch(canvas);
          const blob = await response.blob();
          
          await navigator.clipboard.write([
            new ClipboardItem({
              [blob.type]: blob
            })
          ]);
          
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } else {
          // Fallback: copy text if image copying isn't supported
          await navigator.clipboard.writeText(`"${quote}" - ${author}`);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }
      } catch (error) {
        console.error('Error copying image:', error);
        // Ultimate fallback to text copy
        try {
          if (navigator.clipboard) {
            await navigator.clipboard.writeText(`"${quote}" - ${author}`);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }
        } catch (textError) {
          console.error('Error copying text fallback:', textError);
        }
      }
    }
  };

  const handleBackgroundImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setBackgroundImageUrl(e.target.result);
        setBackgroundImage(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoUrl(e.target.result);
        setLogoFile(file);
        setShowLogo(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const getLogoPositionStyles = () => {
    const baseStyles = {
      position: 'absolute',
      width: `${logoSize}px`,
      height: `${logoSize}px`,
      opacity: logoOpacity,
      objectFit: 'contain',
      zIndex: 20
    };

    switch (logoPosition) {
      case 'top-left':
        return { ...baseStyles, top: '16px', left: '16px' };
      case 'top-right':
        return { ...baseStyles, top: '16px', right: '16px' };
      case 'bottom-left':
        return { ...baseStyles, bottom: '16px', left: '16px' };
      case 'bottom-right':
      default:
        return { ...baseStyles, bottom: '16px', right: '16px' };
    }
  };

  const getBackgroundStyles = () => {
    if (backgroundType === 'solid') {
      return {
        backgroundColor: customBackgroundColor,
        backgroundImage: 'none'
      };
    } else if (backgroundType === 'gradient') {
      return {
        backgroundImage: `linear-gradient(${gradientDirection}, ${gradientColor1}, ${gradientColor2})`,
        backgroundColor: 'transparent'
      };
    } else if (backgroundType === 'image' && backgroundImageUrl) {
      return {
        backgroundImage: `url(${backgroundImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        // Apply blur directly to the background image using filter
        filter: backgroundBlur > 0 ? `blur(${backgroundBlur}px)` : 'none'
      };
    }
    return {};
  };

  const getOverlayStyles = () => {
    if (backgroundOpacity > 0) {
      if ((backgroundType === 'image' && backgroundImageUrl) || backgroundType === 'solid' || backgroundType === 'gradient') {
        return {
          backgroundColor: `rgba(0, 0, 0, ${backgroundOpacity})`,
          // Remove backdrop-filter as it doesn't work with html-to-image
          // backdropFilter: backgroundBlur > 0 ? `blur(${backgroundBlur}px)` : 'none'
        };
      }
    }
    return { display: 'none' };
  };

  // Collapsible panel component
  const CollapsibleSection = ({ title, isExpanded, onToggle, children }) => (
    <div className="border border-neutral-200 rounded-lg">
      <button onClick={onToggle} className="w-full px-4 py-2 flex justify-between bg-neutral-50 hover:bg-neutral-100 rounded-t-lg">
        <span className="font-medium text-neutral-900">{title}</span>
        <svg className={`h-4 w-4 transform ${isExpanded ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.915l3.71-3.685a.75.75 0 111.06 1.06l-4.24 4.21a.75.75 0 01-1.06 0l-4.24-4.21a.75.75 0 01.02-1.06z" clipRule="evenodd"/></svg>
      </button>
      {isExpanded && <div className="p-4 space-y-4">{children}</div>}
    </div>
  );

  // Tab navigation button
  const TabButton = ({ id, label, icon: Icon }) => (
    <button onClick={() => setActiveTab(id)} className={`flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-md transition-colors ${activeTab===id ? 'bg-primary-600 text-white' : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'}`}>
      <Icon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
      <span className="hidden sm:inline">{label}</span>
      <span className="sm:hidden">{label.charAt(0)}</span>
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Quote Card Preview */}
      <div className="flex flex-col items-center">
        <div 
          ref={cardRef}
          className={`relative w-96 h-96 rounded-lg shadow-lg overflow-hidden ${backgroundType === 'theme' ? currentTheme.background : ''}`}
        >
          {/* Background Layer (with blur applied directly) */}
          {backgroundType !== 'theme' && (
            <div 
              className="absolute inset-0 rounded-lg"
              style={getBackgroundStyles()}
            />
          )}
          
          {/* Overlay Layer (for darkness/readability) */}
          {backgroundOpacity > 0 && (backgroundType === 'image' || backgroundType === 'solid' || backgroundType === 'gradient') && (
            <div 
              className="absolute inset-0 rounded-lg"
              style={getOverlayStyles()}
            />
          )}

          {/* Content Layer */}
          <div className="relative z-10 p-8 h-full flex flex-col justify-between">
            {/* Quote Content */}
            <div className="flex-1 flex flex-col justify-center">
              <div 
                className={`mb-4 leading-tight ${!useCustomTextColor && backgroundType === 'theme' ? currentTheme.text : ''} ${!useCustomTextColor && backgroundType !== 'theme' ? 'text-white' : ''}`}
                style={{
                  ...getTextStyles(),
                  textShadow: backgroundType !== 'theme' ? '0 2px 4px rgba(0,0,0,0.3)' : 'none'
                }}
              >
                {quote ? `${quote}"` : 'Your quote will appear here"'}
              </div>
            </div>

            {/* Attribution */}
            <div className="space-y-2">
              <div 
                className={`font-semibold ${backgroundType === 'theme' ? currentTheme.text : 'text-white'}`}
                style={{ fontSize: '18px' }}
              >
                {author ? `— ${author}` : '— Author Name'}
              </div>
              {episode && (
                <div 
                  className={`${backgroundType === 'theme' ? currentTheme.accent : 'text-gray-300'}`}
                  style={{ fontSize: '14px' }}
                >
                  {episode}
                </div>
              )}
              {podcastName && (
                <div 
                  className={`font-medium ${backgroundType === 'theme' ? currentTheme.accent : 'text-gray-300'}`}
                  style={{ fontSize: '14px' }}
                >
                  {podcastName}
                </div>
              )}
            </div>

            {/* Decorative Quote Marks */}
            <div 
              className={`absolute top-6 left-6 ${backgroundType === 'theme' ? currentTheme.accent : 'text-gray-400'} opacity-40 font-serif`}
              style={{ fontSize: '72px' }}
            >
              "
            </div>

            {/* Logo/Watermark */}
            {showLogo && logoUrl && (
              <img 
                src={logoUrl}
                alt="Brand logo"
                style={getLogoPositionStyles()}
                className="pointer-events-none"
              />
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={handleDownload}
            disabled={!quote.trim()}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DownloadIcon className="h-4 w-4 mr-2" />
            Download
          </button>
          
          <button
            onClick={handleCopyImage}
            disabled={!quote.trim()}
            className="inline-flex items-center px-4 py-2 border border-neutral-300 text-neutral-700 bg-white rounded-md hover:bg-neutral-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {copied ? (
              <>
                <CheckIcon className="h-4 w-4 mr-2 text-support-green-500" />
                Copied!
              </>
            ) : (
              <>
                <DuplicateIcon className="h-4 w-4 mr-2" />
                Copy Image
              </>
            )}
          </button>
        </div>
      </div>

      {/* Customization Panel - Tabbed Interface */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
        <div className="border-b border-neutral-200 p-3 sm:p-4">
          <div className="flex flex-wrap gap-1 sm:gap-2">
            <TabButton id="content" label="Content" icon={PencilAltIcon} />
            <TabButton id="design" label="Design" icon={ColorSwatchIcon} />
            <TabButton id="background" label="Background" icon={PhotographIcon} />
            <TabButton id="branding" label="Branding" icon={BadgeCheckIcon} />
            <TabButton id="advanced" label="Advanced" icon={CogIcon} />
          </div>
        </div>
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {activeTab==='content' && (
            <div className="space-y-4 max-w-none lg:max-w-2xl">
              {/* Content Editor */}
              <h3 className="text-lg font-semibold text-neutral-900">
                Quote Content
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Quote Text *
                </label>
                <textarea
                  value={quote}
                  onChange={(e) => setQuote(e.target.value)}
                  className="w-full h-32 p-3 border border-neutral-300 rounded-md resize-none focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Enter your quote here..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Author/Speaker *
                </label>
                <input
                  type="text"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  className="w-full p-2 border border-neutral-300 rounded-md focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Enter author name..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Episode (Optional)
                </label>
                <input
                  type="text"
                  value={episode}
                  onChange={(e) => setEpisode(e.target.value)}
                  className="w-full p-2 border border-neutral-300 rounded-md focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Episode title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Podcast Name (Optional)
                </label>
                <input
                  type="text"
                  value={podcastName}
                  onChange={(e) => setPodcastName(e.target.value)}
                  className="w-full p-2 border border-neutral-300 rounded-md focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Podcast name..."
                />
              </div>
            </div>
          )}
          {activeTab==='design' && (
            <div className="space-y-4 sm:space-y-6">
              <CollapsibleSection title="Typography" isExpanded={expandedSections.textStyle} onToggle={()=>toggleSection('textStyle')}>
                {/* Font Family */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Font Family
                    </label>
                    <select
                      value={fontFamily}
                      onChange={(e) => setFontFamily(e.target.value)}
                      className="w-full p-2 border border-neutral-300 rounded-md focus:border-primary-500 focus:ring-primary-500 text-sm"
                    >
                      {Object.entries(fontCategories).map(([category, categoryData]) => (
                        <optgroup key={category} label={category}>
                          {categoryData.fonts.map((font) => (
                            <option key={font.value} value={font.value}>
                              {font.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <p className="text-xs text-neutral-500 mt-1">
                      {Object.entries(fontCategories).find(([category, categoryData]) => 
                        categoryData.fonts.some(font => font.value === fontFamily)
                      )?.[1]?.description || 'Choose a font style that matches your quote\'s mood'}
                    </p>
                  </div>

                  {/* Font Size */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Font Size: {fontSize}px
                    </label>
                    <input
                      type="range"
                      min="16"
                      max="72"
                      value={fontSize}
                      onChange={(e) => setFontSize(parseInt(e.target.value))}
                      className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-neutral-500 mt-1">
                      <span>16px</span>
                      <span>72px</span>
                    </div>
                  </div>

                  {/* Custom Text Color */}
                  <div>
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="custom-color"
                        checked={useCustomTextColor}
                        onChange={(e) => setUseCustomTextColor(e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                      />
                      <label htmlFor="custom-color" className="ml-2 text-sm font-medium text-neutral-700">
                        Custom Text Color
                      </label>
                    </div>
                    {useCustomTextColor && (
                      <input
                        type="color"
                        value={customTextColor}
                        onChange={(e) => setCustomTextColor(e.target.value)}
                        className="w-full h-10 border border-neutral-300 rounded-md cursor-pointer"
                      />
                    )}
                  </div>

                  {/* Style Options */}
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Text Style
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setIsBold(!isBold)}
                        className={`px-3 py-2 rounded-md text-sm font-bold transition-colors ${
                          isBold 
                            ? 'bg-primary-600 text-white' 
                            : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                        }`}
                      >
                        B
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsItalic(!isItalic)}
                        className={`px-3 py-2 rounded-md text-sm italic transition-colors ${
                          isItalic 
                            ? 'bg-primary-600 text-white' 
                            : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                        }`}
                      >
                        I
                      </button>
                    </div>
                  </div>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Advanced Typography" isExpanded={expandedSections.textAdvanced} onToggle={()=>toggleSection('textAdvanced')}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Letter Spacing */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Letter Spacing: {letterSpacing}px
                    </label>
                    <input
                      type="range"
                      min="-2"
                      max="8"
                      step="0.5"
                      value={letterSpacing}
                      onChange={(e) => setLetterSpacing(parseFloat(e.target.value))}
                      className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-neutral-500 mt-1">
                      <span>-2px</span>
                      <span>8px</span>
                    </div>
                  </div>

                  {/* Line Height */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Line Height: {lineHeight}
                    </label>
                    <input
                      type="range"
                      min="0.8"
                      max="2.0"
                      step="0.1"
                      value={lineHeight}
                      onChange={(e) => setLineHeight(parseFloat(e.target.value))}
                      className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-neutral-500 mt-1">
                      <span>0.8</span>
                      <span>2.0</span>
                    </div>
                  </div>
                </div>
              </CollapsibleSection>
            </div>
          )}
          {activeTab==='background' && (
            <div className="space-y-4 sm:space-y-6">
              <CollapsibleSection title="Background Type" isExpanded={expandedSections.backgroundType} onToggle={()=>toggleSection('backgroundType')}>
                {/* Background Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Background Type
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <button
                      type="button"
                      onClick={() => setBackgroundType('theme')}
                      className={`px-3 py-2 rounded-md transition-colors text-center ${
                        backgroundType === 'theme'
                          ? 'bg-primary-600 text-white'
                          : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                      }`}
                    >
                      Use Theme
                    </button>
                    <button
                      type="button"
                      onClick={() => setBackgroundType('solid')}
                      className={`px-3 py-2 rounded-md transition-colors text-center ${
                        backgroundType === 'solid'
                          ? 'bg-primary-600 text-white'
                          : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                      }`}
                    >
                      Solid Color
                    </button>
                    <button
                      type="button"
                      onClick={() => setBackgroundType('gradient')}
                      className={`px-3 py-2 rounded-md transition-colors text-center ${
                        backgroundType === 'gradient'
                          ? 'bg-primary-600 text-white'
                          : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                      }`}
                    >
                      Gradient
                    </button>
                    <button
                      type="button"
                      onClick={() => setBackgroundType('image')}
                      className={`px-3 py-2 rounded-md transition-colors text-center ${
                        backgroundType === 'image'
                          ? 'bg-primary-600 text-white'
                          : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                      }`}
                    >
                      Custom Image
                    </button>
                  </div>
                </div>

                {/* Theme Selection - Show when theme is selected */}
                {backgroundType === 'theme' && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Choose Theme
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {Object.entries(themes).map(([themeKey, theme]) => (
                        <button
                          key={themeKey}
                          onClick={() => setSelectedTheme(themeKey)}
                          className={`p-3 rounded-lg border-2 transition-all text-left ${
                            selectedTheme === themeKey
                              ? 'border-primary-500 ring-2 ring-primary-200' 
                              : 'border-neutral-200 hover:border-neutral-300'
                          }`}
                        >
                          <div className={`w-full h-8 sm:h-12 rounded ${theme.background} mb-2`} />
                          <div className="text-xs sm:text-sm font-medium text-neutral-700 capitalize">
                            {themeKey.replace('-', ' ')}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </CollapsibleSection>

              <CollapsibleSection title="Background Customization" isExpanded={expandedSections.backgroundCustom} onToggle={()=>toggleSection('backgroundCustom')}>
                <div className="space-y-4">
                  {/* Solid Color Picker */}
                  {backgroundType === 'solid' && (
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Background Color
                      </label>
                      <input
                        type="color"
                        value={customBackgroundColor}
                        onChange={(e) => setCustomBackgroundColor(e.target.value)}
                        className="w-full h-10 border border-neutral-300 rounded-md cursor-pointer"
                      />
                    </div>
                  )}

                  {/* Gradient Configuration */}
                  {backgroundType === 'gradient' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Gradient Direction
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <button
                            type="button"
                            onClick={() => setGradientDirection('to top')}
                            className={`px-2 py-2 text-xs rounded-md transition-colors ${
                              gradientDirection === 'to top'
                                ? 'bg-primary-600 text-white'
                                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                            }`}
                          >
                            ↑ To Top
                          </button>
                          <button
                            type="button"
                            onClick={() => setGradientDirection('to right')}
                            className={`px-2 py-2 text-xs rounded-md transition-colors ${
                              gradientDirection === 'to right'
                                ? 'bg-primary-600 text-white'
                                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                            }`}
                          >
                            → To Right
                          </button>
                          <button
                            type="button"
                            onClick={() => setGradientDirection('to bottom')}
                            className={`px-2 py-2 text-xs rounded-md transition-colors ${
                              gradientDirection === 'to bottom'
                                ? 'bg-primary-600 text-white'
                                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                            }`}
                          >
                            ↓ To Bottom
                          </button>
                          <button
                            type="button"
                            onClick={() => setGradientDirection('to left')}
                            className={`px-2 py-2 text-xs rounded-md transition-colors ${
                              gradientDirection === 'to left'
                                ? 'bg-primary-600 text-white'
                                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                            }`}
                          >
                            ← To Left
                          </button>
                          <button
                            type="button"
                            onClick={() => setGradientDirection('to top right')}
                            className={`px-2 py-2 text-xs rounded-md transition-colors ${
                              gradientDirection === 'to top right'
                                ? 'bg-primary-600 text-white'
                                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                            }`}
                          >
                            ↗ Top Right
                          </button>
                          <button
                            type="button"
                            onClick={() => setGradientDirection('to bottom right')}
                            className={`px-2 py-2 text-xs rounded-md transition-colors ${
                              gradientDirection === 'to bottom right'
                                ? 'bg-primary-600 text-white'
                                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                            }`}
                          >
                            ↘ Bottom Right
                          </button>
                          <button
                            type="button"
                            onClick={() => setGradientDirection('to bottom left')}
                            className={`px-2 py-2 text-xs rounded-md transition-colors ${
                              gradientDirection === 'to bottom left'
                                ? 'bg-primary-600 text-white'
                                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                            }`}
                          >
                            ↙ Bottom Left
                          </button>
                          <button
                            type="button"
                            onClick={() => setGradientDirection('to top left')}
                            className={`px-2 py-2 text-xs rounded-md transition-colors ${
                              gradientDirection === 'to top left'
                                ? 'bg-primary-600 text-white'
                                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                            }`}
                          >
                            ↖ Top Left
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1">
                            Start Color
                          </label>
                          <input
                            type="color"
                            value={gradientColor1}
                            onChange={(e) => setGradientColor1(e.target.value)}
                            className="w-full h-10 border border-neutral-300 rounded-md cursor-pointer"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1">
                            End Color
                          </label>
                          <input
                            type="color"
                            value={gradientColor2}
                            onChange={(e) => setGradientColor2(e.target.value)}
                            className="w-full h-10 border border-neutral-300 rounded-md cursor-pointer"
                          />
                        </div>
                      </div>

                      {/* Gradient Preview */}
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Gradient Preview
                        </label>
                        <div 
                          className="w-full h-16 rounded-md border border-neutral-300"
                          style={{
                            backgroundImage: `linear-gradient(${gradientDirection}, ${gradientColor1}, ${gradientColor2})`
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Image Upload */}
                  {backgroundType === 'image' && (
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Upload Image
                      </label>
                      {/* Safari Warning */}
                      <div className="mb-3 p-3 bg-secondary-50 border border-secondary-200 rounded-md">
                        <div className="flex items-start">
                          <svg className="h-4 w-4 text-secondary-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-secondary-700">Safari Compatibility Issue</p>
                            <p className="text-xs text-secondary-600 mt-1">
                              Custom background images may not render properly in Safari due to a browser bug. For best results, use Chrome, Firefox, or Edge.
                            </p>
                          </div>
                        </div>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleBackgroundImageUpload}
                        className="w-full text-sm text-neutral-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-600 hover:file:bg-primary-100"
                      />
                      {backgroundImageUrl && (
                        <div className="mt-2">
                          <img 
                            src={backgroundImageUrl} 
                            alt="Background preview" 
                            className="w-full h-20 object-cover rounded-md border border-neutral-200"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Overlay Controls (for custom backgrounds) */}
                  {(backgroundType === 'solid' || backgroundType === 'gradient' || (backgroundType === 'image' && backgroundImageUrl)) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">
                          Text Overlay: {Math.round(backgroundOpacity * 100)}%
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="0.8"
                          step="0.1"
                          value={backgroundOpacity}
                          onChange={(e) => setBackgroundOpacity(parseFloat(e.target.value))}
                          className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer slider"
                        />
                        <div className="flex justify-between text-xs text-neutral-500 mt-1">
                          <span>0%</span>
                          <span>80%</span>
                        </div>
                      </div>

                      {backgroundType === 'image' && (
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1">
                            Background Blur: {backgroundBlur}px
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="10"
                            step="1"
                            value={backgroundBlur}
                            onChange={(e) => setBackgroundBlur(parseInt(e.target.value))}
                            className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer slider"
                          />
                          <div className="flex justify-between text-xs text-neutral-500 mt-1">
                            <span>0px</span>
                            <span>10px</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CollapsibleSection>
            </div>
          )}
          {activeTab==='branding' && (
            <div className="space-y-4 sm:space-y-6 max-w-none lg:max-w-2xl">
              {/* Show Logo Toggle */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="show-logo"
                  checked={showLogo}
                  onChange={(e) => setShowLogo(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                />
                <label htmlFor="show-logo" className="ml-2 text-sm font-medium text-neutral-700">
                  Show Brand Logo
                </label>
              </div>

              {/* Logo Upload */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Upload Logo
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="w-full text-sm text-neutral-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-600 hover:file:bg-primary-100"
                />
                {logoUrl && (
                  <div className="mt-2">
                    <img 
                      src={logoUrl} 
                      alt="Logo preview" 
                      className="w-12 h-12 object-contain border border-neutral-200 rounded-md bg-white p-1"
                    />
                  </div>
                )}
              </div>

              {/* Logo Settings */}
              {showLogo && logoUrl && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Logo Position
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <button
                        type="button"
                        onClick={() => setLogoPosition('top-left')}
                        className={`px-2 py-2 text-xs rounded-md transition-colors ${
                          logoPosition === 'top-left'
                            ? 'bg-primary-600 text-white'
                            : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                        }`}
                      >
                        Top Left
                      </button>
                      <button
                        type="button"
                        onClick={() => setLogoPosition('top-right')}
                        className={`px-2 py-2 text-xs rounded-md transition-colors ${
                          logoPosition === 'top-right'
                            ? 'bg-primary-600 text-white'
                            : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                        }`}
                      >
                        Top Right
                      </button>
                      <button
                        type="button"
                        onClick={() => setLogoPosition('bottom-left')}
                        className={`px-2 py-2 text-xs rounded-md transition-colors ${
                          logoPosition === 'bottom-left'
                            ? 'bg-primary-600 text-white'
                            : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                        }`}
                      >
                        Bottom Left
                      </button>
                      <button
                        type="button"
                        onClick={() => setLogoPosition('bottom-right')}
                        className={`px-2 py-2 text-xs rounded-md transition-colors ${
                          logoPosition === 'bottom-right'
                            ? 'bg-primary-600 text-white'
                            : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                        }`}
                      >
                        Bottom Right
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Logo Size */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Logo Size: {logoSize}px
                      </label>
                      <input
                        type="range"
                        min="20"
                        max="100"
                        step="5"
                        value={logoSize}
                        onChange={(e) => setLogoSize(parseInt(e.target.value))}
                        className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <div className="flex justify-between text-xs text-neutral-500 mt-1">
                        <span>20px</span>
                        <span>100px</span>
                      </div>
                    </div>

                    {/* Logo Opacity */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Logo Opacity: {Math.round(logoOpacity * 100)}%
                      </label>
                      <input
                        type="range"
                        min="0.1"
                        max="1.0"
                        step="0.1"
                        value={logoOpacity}
                        onChange={(e) => setLogoOpacity(parseFloat(e.target.value))}
                        className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <div className="flex justify-between text-xs text-neutral-500 mt-1">
                        <span>10%</span>
                        <span>100%</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {activeTab==='advanced' && (
            <div className="space-y-4 sm:space-y-6 max-w-none lg:max-w-2xl">
              {/* Reset Buttons */}
              <div className="pt-4 border-t border-neutral-200 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setFontFamily('ui-sans-serif, system-ui, sans-serif');
                      setFontSize(36);
                      setCustomTextColor('');
                      setUseCustomTextColor(false);
                      setIsBold(false);
                      setIsItalic(false);
                      setLetterSpacing(0);
                      setLineHeight(1.2);
                    }}
                    className="px-3 py-2 text-sm text-neutral-600 bg-neutral-100 rounded-md hover:bg-neutral-200 transition-colors"
                  >
                    Reset Text Styling
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      setBackgroundType('theme');
                      setSelectedTheme('gradient-blue');
                      setCustomBackgroundColor('#4055A8');
                      setBackgroundImage(null);
                      setBackgroundImageUrl('');
                      setBackgroundOpacity(0);
                      setBackgroundBlur(0);
                      setGradientColor1('#4055A8');
                      setGradientColor2('#2D3A8C');
                      setGradientDirection('to bottom right');
                    }}
                    className="px-3 py-2 text-sm text-neutral-600 bg-neutral-100 rounded-md hover:bg-neutral-200 transition-colors"
                  >
                    Reset Background
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setShowLogo(false);
                      setLogoFile(null);
                      setLogoUrl('');
                      setLogoPosition('bottom-right');
                      setLogoSize(40);
                      setLogoOpacity(0.8);
                    }}
                    className="px-3 py-2 text-sm text-neutral-600 bg-neutral-100 rounded-md hover:bg-neutral-200 transition-colors sm:col-span-2 lg:col-span-1"
                  >
                    Reset Branding
                  </button>
                </div>
              </div>

              {/* Tips */}
              <div className="bg-neutral-50 p-4 rounded-lg">
                <h4 className="font-medium text-neutral-900 mb-2">💡 Tips for Great Quote Cards</h4>
                <ul className="text-sm text-neutral-600 space-y-1">
                  <li>• Keep quotes under 40 words for better readability</li>
                  <li>• Choose high contrast backgrounds for social media visibility</li>
                  <li>• Use solid colors, gradients, or overlay for custom images to ensure text readability</li>
                  <li>• Copy the image directly to your clipboard for easy pasting</li>
                  <li>• Perfect for Instagram, Twitter, and LinkedIn posts</li>
                  <li>• Copy quotes from your generated content above</li>
                  <li>• Custom text colors override theme colors - use wisely</li>
                  <li>• Upload high-quality images (1080x1080px recommended)</li>
                  <li>• Adjust overlay and blur for optimal text contrast</li>
                  <li>• <strong>Background Tips:</strong></li>
                  <li>&nbsp;&nbsp;→ Use <em>Gradients</em> for modern, professional looks - diagonal gradients work especially well</li>
                  <li>&nbsp;&nbsp;→ Choose contrasting colors for gradients to create visual depth</li>
                  <li>&nbsp;&nbsp;→ Test different gradient directions to find the most appealing composition</li>
                  <li>&nbsp;&nbsp;→ Use text overlay to ensure readability over bright gradients</li>
                  <li>• <strong>Font Selection Tips:</strong></li>
                  <li>&nbsp;&nbsp;→ Use <em>Sans-Serif</em> fonts for modern, clean, and digital-friendly designs</li>
                  <li>&nbsp;&nbsp;→ Use <em>Serif</em> fonts for formal, academic, or timeless quotes</li>
                  <li>&nbsp;&nbsp;→ Use <em>Display & Impact</em> fonts for powerful statements and headlines</li>
                  <li>&nbsp;&nbsp;→ Use <em>Script & Creative</em> fonts for inspirational or artistic quotes</li>
                  <li>&nbsp;&nbsp;→ Use <em>Monospace</em> fonts for tech content, coding quotes, or modern minimalist designs</li>
                  <li>&nbsp;&nbsp;→ Google Fonts provide professional typography with excellent cross-platform support</li>
                  <li>&nbsp;&nbsp;→ System fonts (first options) provide best performance and native look</li>
                  <li>• Add your brand logo as a watermark for consistent branding</li>
                  <li>• Use PNG logos with transparent backgrounds for best results</li>
                  <li>• Lower logo opacity for subtle watermark effect</li>
                  <li>• Position logos strategically to avoid covering important text</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteCard;
